import React from 'react';

import ExtensionListItem from 'flow/components/ExtensionListItem';
import { useAppSelector } from 'flow/hooks/useRedux';
import useStableCallback from 'flow/hooks/useStableCallback';
import {
    ContactListContact,
    ContactListKey,
    FuzzyMatch,
    selectContactSearchMatches,
} from 'flow/selectors/contact-list-selectors';
import { isEmptyNullOrUndefined } from 'flow/utils';
import { useStoreHoldsRow } from 'flow/components/nav/list-keys/row-index-store';
import { ContactListItemData } from './contact-list-item-data';

const CONTACT_TYPES = {
    extension: 'extension',
    externalContact: 'externalContacts',
    thirdPartyContact: 'socialIntegrationContacts',
} as const;

interface Props {
    index: number;
    kind: keyof typeof CONTACT_TYPES;
    entityKey: ContactListKey;
    data: ContactListItemData;
}

// A store update can run the search again, and a new search text, such as a
// space before the next word, can match the same. Both build new match objects
// with the same content. The row keeps its matches, and does not render again,
// while they are the same. A contact has only a few matches.
export const fuzzyMatchesAreEqual = (a: unknown, b: unknown) =>
    a === b || JSON.stringify(a) === JSON.stringify(b);

// The contact renders again only when one of its own props changes. A change
// to the list's rows, such as a new favourite, a folded section or a new
// search text, renders this row's frame again, and the frame stops here: the
// contact takes values, never the item data, and one click handler for the
// row's life.
const ContactListItem = React.memo(ExtensionListItem);

const ContactListEntityRow = ({ index, kind, entityKey, data }: Props) => {
    const fuzzyMatches = useAppSelector<FuzzyMatch[] | undefined>(
        (state) =>
            !isEmptyNullOrUndefined(data.searchText)
                ? selectContactSearchMatches(state, data.searchText, entityKey)
                : undefined,
        fuzzyMatchesAreEqual,
    );
    const isSelected = useStoreHoldsRow(
        data.selectionStore,
        data.rowKeys[index],
    );
    const isOpen = useStoreHoldsRow(data.openRowStore, data.rowKeys[index]);
    // The list renders this row again whenever its index or the item data
    // change, so a click acts on the row where it is now, also after the list
    // sorts again.
    const onClick = useStableCallback(
        (contact: ContactListContact, status: string) =>
            data.onRowClick(index, entityKey, contact, status),
    );

    return (
        <ContactListItem
            entityKey={entityKey}
            lazyLoad={kind === 'extension' ? false : undefined}
            fuzzyMatches={fuzzyMatches}
            showActionButtons={data.showActionButtons}
            disableIfNoNumber={data.disableItemsWithoutNumber}
            selected={isSelected}
            isOpen={data.marksOpenPage ? isOpen : undefined}
            onClick={onClick}
            contactType={CONTACT_TYPES[kind]}
        />
    );
};

export default ContactListEntityRow;
